import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useAtom } from 'jotai';
import { memo, useCallback, useEffect, useRef, useState } from 'react';
import { Group, Image, Rect, Transformer } from 'react-konva';
import { imageCropEditorAtom } from '@/store/boardAtoms';
import { draftStateAfterUpdate } from '@/utils/board/stageUtils';
import { loadImageFromSrc } from '@/utils/image';
/**
 * Редактор кропа изображения.
 * Реализация основана на примере: https://github.com/pzppzz/konvajs-image-crop-demo
 *
 * Логика:
 * - При первом входе: показываем полное изображение с crop rect на весь элемент
 * - При повторном входе: вычисляем масштаб так, чтобы показать полное изображение,
 *   и позиционируем crop rect так, чтобы он совпадал с текущим отображением
 * - При сохранении: позиция элемента НЕ меняется, только размеры и crop
 */
const ImageCropEditor = ({ boardState, setBoardState, send }) => {
    var _a, _b, _c, _d;
    const transformerRef = useRef(null);
    const cropRectRef = useRef(null);
    const backgroundImageRef = useRef(null);
    const groupRef = useRef(null);
    const [imageCropEditor, setImageCropEditor] = useAtom(imageCropEditorAtom);
    const [backgroundImage, setBackgroundImage] = useState(null);
    const [imagePosition, setImagePosition] = useState({ x: 0, y: 0 });
    const [cropRect, setCropRect] = useState({ x: 0, y: 0, width: 0, height: 0 });
    const [originImageSize, setOriginImageSize] = useState({ width: 0, height: 0 });
    const [isCommitting, setIsCommitting] = useState(false);
    // Флаг ожидания коммита обновленных props элемента, чтобы закрыть оверлей без дергания
    const [pendingClose, setPendingClose] = useState(null);
    // Загрузка изображения
    useEffect(() => {
        var _a, _b;
        if (!imageCropEditor.active || !((_b = (_a = imageCropEditor.element) === null || _a === void 0 ? void 0 : _a.asset) === null || _b === void 0 ? void 0 : _b.url)) {
            setBackgroundImage(null);
            return;
        }
        loadImageFromSrc(imageCropEditor.element.asset.url)
            .then((img) => {
            setBackgroundImage(img);
        })
            .catch((error) => {
            console.error('Failed to load image for crop:', error);
            setBackgroundImage(null);
        });
    }, [imageCropEditor.active, (_b = (_a = imageCropEditor.element) === null || _a === void 0 ? void 0 : _a.asset) === null || _b === void 0 ? void 0 : _b.url]);
    // Инициализация позиции изображения и области кропа
    useEffect(() => {
        var _a, _b, _c, _d;
        if (!imageCropEditor.active || !imageCropEditor.element || !backgroundImage)
            return;
        // Используем актуальный элемент из boardState для правильного расчета координат
        let actualElement = imageCropEditor.element;
        for (const layer of boardState.layers || []) {
            const found = (_a = layer.elements) === null || _a === void 0 ? void 0 : _a.find((el) => el.id === actualElement.id);
            if (found) {
                actualElement = found;
                break;
            }
        }
        const existingCrop = (_b = actualElement.props) === null || _b === void 0 ? void 0 : _b.crop;
        const elementW = ((_c = actualElement.props) === null || _c === void 0 ? void 0 : _c.w) || 64;
        const elementH = ((_d = actualElement.props) === null || _d === void 0 ? void 0 : _d.h) || 64;
        const imageWidth = backgroundImage.naturalWidth;
        const imageHeight = backgroundImage.naturalHeight;
        if (existingCrop) {
            // Есть существующий crop
            // Логика из CropScene.show():
            // ratio = selectedImage.width() / selectedImage.cropWidth()
            // originWidth = ratio * selectedImage.image().width
            // cropX = selectedImage.cropX() * ratio
            // ratio - это соотношение между размером отображаемого изображения и размером crop области
            // После кропа размеры элемента = размерам crop области
            const ratio = elementW / existingCrop.width;
            // originWidth/originHeight - это размеры полного изображения в масштабе редактора
            const originWidth = ratio * imageWidth;
            const originHeight = ratio * imageHeight;
            // cropX/cropY в масштабе редактора (в координатах исходного изображения * ratio)
            const cropX = existingCrop.x * ratio;
            const cropY = existingCrop.y * ratio;
            // Размеры crop rect в масштабе редактора (равны размерам элемента)
            const cropWidth = elementW;
            const cropHeight = elementH;
            // Позиция изображения: чтобы crop область совпала с позицией элемента
            // При повторном входе элемент уже находится на правильной позиции (x + cropX, y + cropY)
            // Но crop rect в редакторе должен быть на (0, 0), поэтому изображение нужно сместить на -cropX/-cropY
            // Но нужно учесть, что изображение может быть больше элемента, поэтому позиция может быть отрицательной
            const imageX = -cropX;
            const imageY = -cropY;
            setImagePosition({ x: imageX, y: imageY });
            setOriginImageSize({ width: originWidth, height: originHeight });
            setCropRect({
                x: 0,
                y: 0,
                width: cropWidth,
                height: cropHeight,
            });
        }
        else {
            // Кропа нет - показываем изображение полностью
            const scaleX = elementW / imageWidth;
            const scaleY = elementH / imageHeight;
            const scale = Math.min(scaleX, scaleY);
            const scaledImageWidth = imageWidth * scale;
            const scaledImageHeight = imageHeight * scale;
            const imageX = (elementW - scaledImageWidth) / 2;
            const imageY = (elementH - scaledImageHeight) / 2;
            setImagePosition({ x: imageX, y: imageY });
            setOriginImageSize({ width: scaledImageWidth, height: scaledImageHeight });
            setCropRect({
                x: 0,
                y: 0,
                width: elementW,
                height: elementH,
            });
        }
    }, [imageCropEditor.active, imageCropEditor.element, backgroundImage, boardState]);
    // Применяем трансформер к прямоугольнику кропа (без задержек)
    useEffect(() => {
        var _a;
        if (!imageCropEditor.active || !imageCropEditor.element || !backgroundImage)
            return;
        const transformer = transformerRef.current;
        const cropRectNode = cropRectRef.current;
        if (!transformer || !cropRectNode)
            return;
        transformer.nodes([cropRectNode]);
        (_a = transformer.getLayer()) === null || _a === void 0 ? void 0 : _a.batchDraw();
    }, [imageCropEditor.active, imageCropEditor.element, backgroundImage]);
    // Сохранение кропа и отправка на сервер
    const saveCrop = useCallback(() => {
        var _a, _b, _c;
        if (!imageCropEditor.active || !imageCropEditor.element || !backgroundImage)
            return;
        const cropRectNode = cropRectRef.current;
        const backgroundImageNode = backgroundImageRef.current;
        const groupNode = groupRef.current;
        if (!cropRectNode || !backgroundImageNode || !groupNode)
            return;
        // Мгновенно скрываем визуальный редактор перед применением, чтобы исключить любой кадр с пересчётами
        setIsCommitting(true);
        if (transformerRef.current)
            transformerRef.current.nodes([]);
        try {
            groupNode.visible(false);
            groupNode.listening(false);
            (_a = groupNode.getLayer()) === null || _a === void 0 ? void 0 : _a.batchDraw();
        }
        catch (e) {
            // ignore
        }
        const element = imageCropEditor.element;
        const imageWidth = backgroundImage.naturalWidth;
        const imageHeight = backgroundImage.naturalHeight;
        // Получаем актуальные размеры и позицию crop rect относительно элемента
        const cropX = cropRectNode.x();
        const cropY = cropRectNode.y();
        let cropWidth = cropRectNode.width() * cropRectNode.scaleX();
        let cropHeight = cropRectNode.height() * cropRectNode.scaleY();
        // ВАЖНО: Получаем актуальную позицию изображения из ref, а не из state
        // Это гарантирует, что мы используем правильные координаты при сохранении
        const actualImageX = backgroundImageNode.x();
        const actualImageY = backgroundImageNode.y();
        // Позиция crop rect относительно изображения
        let cropRelativeToImageX = cropX - actualImageX;
        let cropRelativeToImageY = cropY - actualImageY;
        // ОГРАНИЧИВАЕМ КРОП РАЗМЕРАМИ ИЗОБРАЖЕНИЯ
        // Это предотвращает создание кропа с пустыми границами
        const maxImageWidth = originImageSize.width;
        const maxImageHeight = originImageSize.height;
        // Ограничиваем позицию левой и верхней границей
        if (cropRelativeToImageX < 0) {
            cropWidth += cropRelativeToImageX;
            cropRelativeToImageX = 0;
        }
        if (cropRelativeToImageY < 0) {
            cropHeight += cropRelativeToImageY;
            cropRelativeToImageY = 0;
        }
        // Ограничиваем размеры правой и нижней границей
        if (cropRelativeToImageX + cropWidth > maxImageWidth) {
            cropWidth = maxImageWidth - cropRelativeToImageX;
        }
        if (cropRelativeToImageY + cropHeight > maxImageHeight) {
            cropHeight = maxImageHeight - cropRelativeToImageY;
        }
        // Минимальные размеры
        cropWidth = Math.max(10, cropWidth);
        cropHeight = Math.max(10, cropHeight);
        // Логика из _handleCropEnd():
        // ratio = this._originImage.width() / image.width
        // cropX = this._clipRect.x() / ratio
        // cropWidth = (width * image.width) / this._originImage.width()
        const ratio = originImageSize.width / imageWidth;
        // Переводим из координат редактора в координаты исходного изображения
        const finalCropX = cropRelativeToImageX / ratio;
        const finalCropY = cropRelativeToImageY / ratio;
        const finalCropWidth = (cropWidth * imageWidth) / originImageSize.width;
        const finalCropHeight = (cropHeight * imageHeight) / originImageSize.height;
        // Обновляем размеры элемента под размеры кропа (в координатах элемента)
        const newW = Math.max(10, cropWidth);
        const newH = Math.max(10, cropHeight);
        // КОРРЕКТИРУЕМ ПОЗИЦИЮ ЭЛЕМЕНТА
        // ВАЖНО: Позиция элемента должна быть равна абсолютной позиции crop rect на доске
        // Group находится на позиции элемента (elementX, elementY)
        // Crop rect находится на позиции (cropX, cropY) относительно Group
        // Поэтому абсолютная позиция crop rect = elementX + cropX, elementY + cropY
        // Но нужно учесть, что crop rect может быть на (0,0) при первом кропе или при повторном входе
        const currentElementX = ((_b = element.props) === null || _b === void 0 ? void 0 : _b.x) || 0;
        const currentElementY = ((_c = element.props) === null || _c === void 0 ? void 0 : _c.y) || 0;
        // Абсолютная позиция crop rect на доске: используем ИМЕННО откорректированные координаты прямоугольника
        // (внутри изображения после ограничения), чтобы избежать прыжка при выходе за левую/верхнюю границы
        const correctedRectX = actualImageX + cropRelativeToImageX;
        const correctedRectY = actualImageY + cropRelativeToImageY;
        const absoluteCropX = currentElementX + correctedRectX;
        const absoluteCropY = currentElementY + correctedRectY;
        // ВАЖНО: Позиция элемента устанавливается на абсолютную позицию crop rect
        // Это обеспечивает, что обрезанное изображение останется на том же месте на доске
        const props = Object.assign(Object.assign({}, element.props), { x: absoluteCropX, y: absoluteCropY, w: newW, h: newH, crop: {
                x: finalCropX,
                y: finalCropY,
                width: finalCropWidth,
                height: finalCropHeight,
            } });
        const updates = [
            {
                id: element.id,
                props,
            },
        ];
        // На время применения скрываем редактор, чтобы исключить дергания кроппера/фона
        setIsCommitting(true);
        // Используем draftStateAfterUpdate для правильного обновления состояния
        setBoardState((prev) => draftStateAfterUpdate(prev, updates));
        send({
            type: 'element.update',
            payload: updates,
        });
        // Ждем, пока элемент в boardState примет нужные значения, затем закроем оверлей
        setPendingClose({
            id: element.id,
            x: absoluteCropX,
            y: absoluteCropY,
            w: newW,
            h: newH,
            crop: { x: finalCropX, y: finalCropY, width: finalCropWidth, height: finalCropHeight },
        });
    }, [
        imageCropEditor.active,
        imageCropEditor.element,
        backgroundImage,
        originImageSize,
        setBoardState,
        send,
    ]);
    // Закрываем оверлей, когда состояние элемента действительно обновилось (избегаем визуального дергания)
    useEffect(() => {
        var _a, _b, _c, _d, _e, _f;
        if (!pendingClose)
            return;
        const { id, x, y, w, h, crop } = pendingClose;
        for (const layer of boardState.layers || []) {
            const el = (_a = layer.elements) === null || _a === void 0 ? void 0 : _a.find((e) => e.id === id);
            if (!el)
                continue;
            const ex = ((_b = el.props) === null || _b === void 0 ? void 0 : _b.x) || 0;
            const ey = ((_c = el.props) === null || _c === void 0 ? void 0 : _c.y) || 0;
            const ew = ((_d = el.props) === null || _d === void 0 ? void 0 : _d.w) || 0;
            const eh = ((_e = el.props) === null || _e === void 0 ? void 0 : _e.h) || 0;
            const ec = ((_f = el.props) === null || _f === void 0 ? void 0 : _f.crop) || null;
            const cropEqual = !!ec &&
                Math.abs(ec.x - crop.x) < 0.01 &&
                Math.abs(ec.y - crop.y) < 0.01 &&
                Math.abs(ec.width - crop.width) < 0.01 &&
                Math.abs(ec.height - crop.height) < 0.01;
            if (Math.abs(ex - x) < 0.01 &&
                Math.abs(ey - y) < 0.01 &&
                Math.abs(ew - w) < 0.01 &&
                Math.abs(eh - h) < 0.01 &&
                cropEqual) {
                // Закрываем через два кадра, чтобы ImageGroup успел пересобрать кэш под новый crop
                setPendingClose(null);
                requestAnimationFrame(() => {
                    requestAnimationFrame(() => {
                        setIsCommitting(false);
                        setImageCropEditor({ active: false, element: null });
                    });
                });
                break;
            }
        }
    }, [boardState, pendingClose, setImageCropEditor]);
    // Обработчик клика вне трансформера кропа
    const handleStageClick = useCallback((event) => {
        var _a;
        if (!imageCropEditor.active)
            return;
        const stage = (_a = groupRef.current) === null || _a === void 0 ? void 0 : _a.getStage();
        if (!stage)
            return;
        let target = event.target;
        let isCropTransformerClick = false;
        while (target) {
            if (target.name() === 'crop-rect' ||
                target.name() === 'crop-transformer' ||
                target.name() === 'crop-background-image') {
                isCropTransformerClick = true;
                break;
            }
            if (!target.parent)
                break;
            target = target.parent;
        }
        if (!isCropTransformerClick) {
            saveCrop();
        }
    }, [imageCropEditor.active, saveCrop]);
    useEffect(() => {
        var _a;
        if (!imageCropEditor.active)
            return;
        const stage = (_a = groupRef.current) === null || _a === void 0 ? void 0 : _a.getStage();
        if (!stage)
            return;
        const timeoutId = setTimeout(() => {
            stage.on('mousedown', handleStageClick);
        }, 100);
        return () => {
            clearTimeout(timeoutId);
            stage.off('mousedown', handleStageClick);
        };
    }, [imageCropEditor.active, handleStageClick]);
    // Обработчик завершения трансформации области кропа
    const handleCropTransformEnd = useCallback(() => {
        var _a;
        if (!imageCropEditor.active || !cropRectRef.current)
            return;
        const cropRectNode = cropRectRef.current;
        const scaleX = cropRectNode.scaleX();
        const scaleY = cropRectNode.scaleY();
        const newWidth = cropRectNode.width() * scaleX;
        const newHeight = cropRectNode.height() * scaleY;
        cropRectNode.scaleX(1);
        cropRectNode.scaleY(1);
        cropRectNode.width(newWidth);
        cropRectNode.height(newHeight);
        setCropRect({
            x: cropRectNode.x(),
            y: cropRectNode.y(),
            width: newWidth,
            height: newHeight,
        });
        if (transformerRef.current) {
            transformerRef.current.nodes([cropRectNode]);
        }
        (_a = cropRectNode.getLayer()) === null || _a === void 0 ? void 0 : _a.batchDraw();
    }, [imageCropEditor.active]);
    // Обработчик перемещения crop rect (ограничиваем размерами изображения)
    const handleCropRectDragMove = useCallback(() => {
        if (!cropRectRef.current)
            return;
        const rect = cropRectRef.current;
        const x = rect.x();
        const y = rect.y();
        let width = rect.width();
        let height = rect.height();
        const originWidth = originImageSize.width;
        const originHeight = originImageSize.height;
        // Позиция crop rect относительно изображения
        const cropRelativeToImageX = x - imagePosition.x;
        const cropRelativeToImageY = y - imagePosition.y;
        // Ограничиваем позицию размерами изображения (логика из _handleDrag)
        let newX = cropRelativeToImageX;
        let newY = cropRelativeToImageY;
        if (newX < 0) {
            newX = 0;
        }
        if (newX + width > originWidth) {
            newX = originWidth - width;
            width = originWidth - newX;
        }
        if (newY < 0) {
            newY = 0;
        }
        if (newY + height > originHeight) {
            newY = originHeight - height;
            height = originHeight - newY;
        }
        // Переводим обратно в координаты элемента
        const finalX = newX + imagePosition.x;
        const finalY = newY + imagePosition.y;
        rect.setAttrs({
            x: finalX,
            y: finalY,
            width,
            height,
        });
        setCropRect({
            x: finalX,
            y: finalY,
            width,
            height,
        });
    }, [imagePosition, originImageSize]);
    // Обработчик завершения перемещения crop rect
    const handleCropRectDragEnd = useCallback(() => {
        if (!cropRectRef.current)
            return;
        const rect = cropRectRef.current;
        setCropRect({
            x: rect.x(),
            y: rect.y(),
            width: rect.width(),
            height: rect.height(),
        });
    }, []);
    // Ограничиваем область кропа размерами изображения в редакторе
    // Функция для получения границ изображения в абсолютных координатах Stage
    // ВАЖНО: anchorDragBoundFunc получает абсолютные координаты якоря относительно Stage
    // Используем getClientRect() для получения реальных размеров изображения с учетом всех трансформаций
    const getImageBounds = useCallback(() => {
        if (!backgroundImageRef.current) {
            return null;
        }
        const imageNode = backgroundImageRef.current;
        // Получаем реальные размеры изображения с учетом scale и других трансформаций
        const clientRect = imageNode.getClientRect();
        if (!clientRect || clientRect.width <= 0 || clientRect.height <= 0) {
            // Fallback на обычные размеры, если getClientRect не работает
            let imageWidth = imageNode.width();
            let imageHeight = imageNode.height();
            if (imageWidth <= 0 || imageHeight <= 0 || isNaN(imageWidth) || isNaN(imageHeight)) {
                imageWidth = originImageSize.width;
                imageHeight = originImageSize.height;
            }
            if (imageWidth <= 0 || imageHeight <= 0) {
                return null;
            }
            const imageAbsPos = imageNode.getAbsolutePosition();
            return {
                left: imageAbsPos.x,
                right: imageAbsPos.x + imageWidth,
                top: imageAbsPos.y,
                bottom: imageAbsPos.y + imageHeight,
            };
        }
        // Используем getClientRect для получения реальных границ с учетом трансформаций
        return {
            left: clientRect.x,
            right: clientRect.x + clientRect.width,
            top: clientRect.y,
            bottom: clientRect.y + clientRect.height,
        };
    }, [originImageSize.width, originImageSize.height]);
    // Ограничение позиции якорей границами изображения
    // ВАЖНО: anchorDragBoundFunc получает абсолютные координаты якоря относительно Stage
    // Координаты уже учитывают трансформации Stage (zoom, pan)
    const anchorDragBoundFunc = useCallback((oldAbsPos, newAbsPos) => {
        const bounds = getImageBounds();
        if (!bounds) {
            return newAbsPos;
        }
        // Используем небольшую погрешность для учета ошибок округления
        const buffer = 0.5;
        const minX = bounds.left - buffer;
        const maxX = bounds.right + buffer;
        const minY = bounds.top - buffer;
        const maxY = bounds.bottom + buffer;
        // Ограничиваем позицию якоря границами изображения
        // Если новая позиция в пределах границ - разрешаем, иначе ограничиваем
        let x = newAbsPos.x;
        let y = newAbsPos.y;
        // Ограничиваем по горизонтали
        if (x < minX) {
            x = minX;
        }
        else if (x > maxX) {
            x = maxX;
        }
        // Ограничиваем по вертикали
        if (y < minY) {
            y = minY;
        }
        else if (y > maxY) {
            y = maxY;
        }
        return { x, y };
    }, [getImageBounds]);
    // Упрощенная boundBoxFunc - только нормализация и минимальные размеры
    // Ограничения для якорей перенесены в anchorDragBoundFunc
    const boundBoxFunc = useCallback((oldBox, newBox) => {
        // Нормализуем координаты newBox (обрабатываем отрицательные размеры)
        const { rotation } = newBox;
        let cropX = newBox.x;
        let cropY = newBox.y;
        let cropWidth = newBox.width;
        let cropHeight = newBox.height;
        if (cropWidth < 0) {
            cropX += cropWidth;
            cropWidth = -cropWidth;
        }
        if (cropHeight < 0) {
            cropY += cropHeight;
            cropHeight = -cropHeight;
        }
        // Проверяем минимальные размеры
        if (cropWidth < 10 || cropHeight < 10) {
            return oldBox;
        }
        // Разрешаем изменение (ограничения для якорей в anchorDragBoundFunc)
        return {
            x: cropX,
            y: cropY,
            width: cropWidth,
            height: cropHeight,
            rotation: rotation !== null && rotation !== void 0 ? rotation : 0,
        };
    }, []);
    // Обработчик перемещения фонового изображения
    const handleBackgroundImageDragEnd = useCallback(() => {
        if (!backgroundImageRef.current)
            return;
        const img = backgroundImageRef.current;
        setImagePosition({ x: img.x(), y: img.y() });
    }, []);
    if (!imageCropEditor.active || !imageCropEditor.element || isCommitting) {
        return null;
    }
    const element = imageCropEditor.element;
    const elementX = ((_c = element.props) === null || _c === void 0 ? void 0 : _c.x) || 0;
    const elementY = ((_d = element.props) === null || _d === void 0 ? void 0 : _d.y) || 0;
    return (_jsxs(Group, { ref: groupRef, x: elementX, y: elementY, name: "crop-editor-group", listening: true, children: [backgroundImage && (_jsx(Image, { ref: backgroundImageRef, image: backgroundImage, x: imagePosition.x, y: imagePosition.y, width: originImageSize.width, height: originImageSize.height, name: "crop-background-image", draggable: false, listening: false, onDragEnd: handleBackgroundImageDragEnd, opacity: 0.35 })), _jsx(Rect, { ref: cropRectRef, x: cropRect.x, y: cropRect.y, width: cropRect.width, height: cropRect.height, fill: "rgba(0,0,0,0.1)", stroke: "#5eafffff", strokeWidth: 2, name: "crop-rect", strokeScaleEnabled: false, shadowForStrokeEnabled: false, perfectDrawEnabled: false, draggable: true, onDragMove: handleCropRectDragMove, onDragEnd: handleCropRectDragEnd, onTransformEnd: handleCropTransformEnd }), _jsx(Transformer, { ref: transformerRef, name: "crop-transformer", borderEnabled: false, anchorFill: "#5eafffff", anchorStroke: "#ffffff", anchorSize: 12, strokeScaleEnabled: true, shadowForStrokeEnabled: false, perfectDrawEnabled: false, keepRatio: false, rotateEnabled: false, flipEnabled: false, ignoreStroke: true, boundBoxFunc: boundBoxFunc, anchorDragBoundFunc: anchorDragBoundFunc, onTransformEnd: handleCropTransformEnd })] }));
};
export default memo(ImageCropEditor);
